// src/features/dashboard/hooks/useHeatmap.ts

import {
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  ENV,
} from '../../../config/env';

import {
  heatmapMockData,
} from '../mocks/heatmap.mock';

import {
  getHeatmapData,
} from '../services/heatmap.service';

import type {
  HeatmapApiRow,
  HeatmapRow,
} from '../types/heatmap.types';

import {
  useManagerDate,
  formatManagerDate,
} from '../context/ManagerDateContext';


/* ============================================================
   PARAMS
============================================================ */

type Params = {
  managerEmployeeCode: string;
};


/* ============================================================
   HOOK
============================================================ */

export const useHeatmap = ({
  managerEmployeeCode,
}: Params) => {

  /* ==========================================================
     GET MANAGER DATES
     
     IMPORTANT:
     
     This state changes whenever DashboardHeader changes
     the selected date.
  ========================================================== */

  const {
    fromDate,
    toDate,
  } = useManagerDate();


  /* ==========================================================
     FORMAT API DATES
     
     NEVER use:
     
       toISOString()
     
     because that can change the date because of UTC.
  ========================================================== */

  const fromDateString =
    formatManagerDate(
      fromDate,
    );

  const toDateString =
    formatManagerDate(
      toDate,
    );


  /* ==========================================================
     EMPLOYEE CODE
  ========================================================== */

  const employeeCode =
    String(
      managerEmployeeCode ?? '',
    ).trim();


  /* ==========================================================
     STATE
  ========================================================== */

  const [
    rows,
    setRows,
  ] = useState<HeatmapRow[]>([]);

  const [
    loading,
    setLoading,
  ] = useState<boolean>(false);

  const [
    error,
    setError,
  ] = useState<string>('');


  /* ==========================================================
     TRANSFORM API RESPONSE
  ========================================================== */

  const transform =
    useCallback(
      (
        data: HeatmapApiRow[],
      ): HeatmapRow[] => {

        if (
          !Array.isArray(
            data,
          )
        ) {
          return [];
        }

        return data.map(
          (
            item,
          ) => {

            const activity = [
              item.hour00,
              item.hour01,
              item.hour02,
              item.hour03,
              item.hour04,
              item.hour05,
              item.hour06,
              item.hour07,
              item.hour08,
              item.hour09,
              item.hour10,
              item.hour11,
              item.hour12,
              item.hour13,
              item.hour14,
              item.hour15,
              item.hour16,
              item.hour17,
              item.hour18,
              item.hour19,
              item.hour20,
              item.hour21,
              item.hour22,
              item.hour23,
            ].map(
              (
                value,
              ) => {

                if (
                  value === null ||
                  value === undefined
                ) {
                  return 0;
                }

                const numericValue =
                  Number(
                    value,
                  );

                if (
                  Number.isNaN(
                    numericValue,
                  )
                ) {
                  return 0;
                }

                return numericValue;
              },
            );


            return {
              name:
                item.teamLeadName,

              hc:
                item.hc,

              shift:
                '',

              activity,
            };
          },
        );
      },
      [],
    );


  /* ==========================================================
     FETCH HEATMAP
  ========================================================== */

  const fetchHeatmap =
    useCallback(
      async () => {

        /* ====================================================
           VALIDATE
        ==================================================== */

        if (
          !employeeCode
        ) {

          console.warn(
            'HEATMAP API SKIPPED: Employee code is missing.',
          );

          setRows([]);

          setLoading(
            false,
          );

          return;
        }


        if (
          !fromDateString ||
          !toDateString
        ) {

          console.warn(
            'HEATMAP API SKIPPED: Date is missing.',
            {
              fromDate:
                fromDateString,

              toDate:
                toDateString,
            },
          );

          setRows([]);

          setLoading(
            false,
          );

          return;
        }


        /* ====================================================
           DATE RANGE VALIDATION
        ==================================================== */

        if (
          fromDateString >
          toDateString
        ) {

          console.warn(
            'HEATMAP API SKIPPED: Invalid date range.',
            {
              fromDate:
                fromDateString,

              toDate:
                toDateString,
            },
          );

          setRows([]);

          setError(
            'From date cannot be greater than To date.',
          );

          setLoading(
            false,
          );

          return;
        }


        /* ====================================================
           LOADING
        ==================================================== */

        setLoading(
          true,
        );

        setError('');

        setRows([]);


        /* ====================================================
           DEBUG
        ==================================================== */

        console.log(
          '==========================================',
        );

        console.log(
          'HEATMAP API CALL',
        );

        console.log(
          'Manager Employee Code:',
          employeeCode,
        );

        console.log(
          'From Date:',
          fromDateString,
        );

        console.log(
          'To Date:',
          toDateString,
        );

        console.log(
          'USE MOCK:',
          ENV.USE_MOCK,
        );

        console.log(
          '==========================================',
        );


        try {

          /* ==================================================
             MOCK
          ================================================== */

          if (
            ENV.USE_MOCK
          ) {

            console.warn(
              'HEATMAP API NOT CALLED because ENV.USE_MOCK = true.',
            );

            setRows(
              transform(
                heatmapMockData,
              ),
            );

            return;
          }


          /* ==================================================
             API PAYLOAD
          ================================================== */

          const payload = {
            managerEmployeeCode:
              employeeCode,

            fromDate:
              fromDateString,

            toDate:
              toDateString,
          };


          console.log(
            'HEATMAP REQUEST PAYLOAD:',
            payload,
          );


          /* ==================================================
             API CALL
          ================================================== */

          const response =
            await getHeatmapData(
              payload,
            );


          console.log(
            'HEATMAP RESPONSE:',
            response,
          );


          /* ==================================================
             TRANSFORM
          ================================================== */

          const transformedRows =
            transform(
              response ?? [],
            );


          /* ==================================================
             SET DATA
          ================================================== */

          setRows(
            transformedRows,
          );

        } catch (
          err: any
        ) {

          console.error(
            'HEATMAP API ERROR:',
            err,
          );

          setRows([]);

          setError(
            err?.response?.data?.message ||
            err?.message ||
            'Failed to load heatmap data',
          );

        } finally {

          setLoading(
            false,
          );
        }

      },
      [
        employeeCode,
        fromDateString,
        toDateString,
        transform,
      ],
    );


  /* ==========================================================
     AUTOMATIC API CALL
     
     This is the critical part.
     
     When:
     
       fromDateString changes
              OR
       toDateString changes
     
     fetchHeatmap changes.
     
     Therefore this useEffect runs again.
     
     Therefore API is called again.
  ========================================================== */

  useEffect(
    () => {

      fetchHeatmap();

    },
    [
      fetchHeatmap,
    ],
  );


  /* ==========================================================
     RETURN
  ========================================================== */

  return {
    rows,
    loading,
    error,
    refetch:
      fetchHeatmap,
  };
};